/**
 * Shared data-fetching hooks for the admin console.
 * Auto-refreshes on a configurable interval with last-refreshed tracking.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSystemHealth,
  getPipelineStatus,
  getMetricsSummary,
  getFeedbackStats,
  getWebhookSubscriptions,
  getIndexerInvocations,
  getConversations,
  getConversationStats,
} from '../../../api/adminApi';
import type {
  SystemHealthResponse,
  PipelineStatusResponse,
  MetricsSummaryResponse,
  FeedbackStatsResponse,
  WebhookSubscriptionsResponse,
  IndexerInvocationsResponse,
  ConversationListResponse,
  ConversationStatsResponse,
  ConversationListParams,
} from '../../../types/admin';

// ============================================================================
// Generic polling hook
// ============================================================================

interface UsePollingResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastRefreshed: Date | null;
  refresh: () => Promise<void>;
}

function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number = 60_000
): UsePollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const result = await fetcher();
      if (mountedRef.current) {
        setData(result);
        setLastRefreshed(new Date());
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Fetch failed');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [refresh, intervalMs]);

  return { data, loading, error, lastRefreshed, refresh };
}

// ============================================================================
// Domain-specific hooks
// ============================================================================

export function useSystemHealth(intervalMs = 60_000) {
  return usePolling<SystemHealthResponse>(getSystemHealth, intervalMs);
}

export function usePipelineStatus(intervalMs = 60_000) {
  return usePolling<PipelineStatusResponse>(getPipelineStatus, intervalMs);
}

export function useMetrics(hours = 720, intervalMs = 120_000) {
  const fetcher = useCallback(() => getMetricsSummary(hours), [hours]);
  return usePolling<MetricsSummaryResponse>(fetcher, intervalMs);
}

export function useFeedback(days = 30, intervalMs = 120_000) {
  const fetcher = useCallback(() => getFeedbackStats(days), [days]);
  return usePolling<FeedbackStatsResponse>(fetcher, intervalMs);
}

// --- Conversation Review (F19) ---

// NOTE: params is an object literal, so we CANNOT use [params] as the dep —
// a caller passing an inline object (useConversations({ limit: 50 })) would create
// a new identity each render, re-firing the poll effect every render (poll-storm).
// Depend on the destructured primitive fields instead: correct regardless of how
// the caller constructs the object. (useMetrics dodges this because `hours` is primitive.)
export function useConversations(
  params: ConversationListParams = {},
  intervalMs = 120_000
) {
  const { user, from, to, search, limit, offset } = params;
  const fetcher = useCallback(
    () => getConversations({ user, from, to, search, limit, offset }),
    [user, from, to, search, limit, offset]
  );
  return usePolling<ConversationListResponse>(fetcher, intervalMs);
}

// Primitive param — verbatim useMetrics twin, no object-identity snag.
export function useConversationStats(days = 30, intervalMs = 120_000) {
  const fetcher = useCallback(() => getConversationStats(days), [days]);
  return usePolling<ConversationStatsResponse>(fetcher, intervalMs);
}

export function useWebhookSubscriptions(intervalMs = 60_000) {
  return usePolling<WebhookSubscriptionsResponse>(getWebhookSubscriptions, intervalMs);
}

export function useIndexerInvocations(intervalMs = 60_000) {
  return usePolling<IndexerInvocationsResponse>(getIndexerInvocations, intervalMs);
}

// ============================================================================
// Utility: format "last refreshed" as relative time
// ============================================================================

export function formatRelativeTime(date: Date | null): string {
  if (!date) return 'never';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}
